import { TranslateService } from "@ngx-translate/core";
import { GridComponent, SaveEventArgs, SearchSettingsModel } from "@syncfusion/ej2-angular-grids";
import { Subject, startWith, takeUntil } from "rxjs";
import { ShowInactiveService } from "./show-inactive.service";
import { ClientService } from "../../services/client/client.service";
import { GridConstantsFactory } from "../syncfusion/grid-constants-factory";
import { Component, ViewChild } from "@angular/core";
import { UntypedFormGroup } from "@angular/forms";
import { SfRequestTypes } from "../../models/syncfusion-data";

@Component({
  selector: 'app-grid-local-data-base',
  template: ''
})
export abstract class GridLocalDataBaseComponent {
  @ViewChild('grid')
  public grid: GridComponent;
  public filterSettings = GridConstantsFactory.createStandardFilterSettings();
  public editSettings = GridConstantsFactory.createStandardEditSettings();
  public pageSettings = GridConstantsFactory.createStandardPageSettings();
  public gridCommands = GridConstantsFactory.createStandardGridCommands();
  public searchOptions: SearchSettingsModel;
  public gridEditForm: UntypedFormGroup;
  public isActiveFilter = GridConstantsFactory.createCheckBoxFilter();

  public formatIsActive(data: any, isActiveField: string = 'active'): boolean {
    if (data && data[isActiveField]) {
      return this.translateService.instant('i18n.shared.fields.active');
    }
    return this.translateService.instant('i18n.shared.inactive');
  }

  public get showInactive(): boolean {
    // due to a bug in syncfusion we can only present a value that is only initialized to syncfusion but we want to still keep the showInactive information in the service
    if (this._showInactive == null) {
      this._showInactive = this.showInactiveService.showInactive;
    }
    return this._showInactive;
  }
  public set showInactive(value: boolean) {
    if (this.showInactiveService.showInactive !== value) {
      this.showInactiveService.showInactive = value;
      // syncfusion uses [visible] only to initialize the grid
      this.grid.getColumnByField(this.isActiveColumnName ?? 'active').visible = value;
      this.grid.refreshColumns();
    }
  }
  private _showInactive: boolean = null;

  /* data to be provided by childclass */
  public data: any[];
  protected isActiveColumnName: string;
  protected editDialogTitle: string = 'editDialogTitle';
  protected addDialogTitle: string = 'addDialogTitle';
  protected addIndexFindColumnName: string = 'name';
  /* data to be provided by childclass */

  protected unsubscribeSubject = new Subject();
  protected formInitialValue;
  protected idOfEditedObject: number = null;
  protected cssClass: string = null;

  constructor(
    protected clientService: ClientService,
    protected translateService: TranslateService,
    protected showInactiveService: ShowInactiveService
  ) { }

  public onAdd() {
    this.grid.addRecord();
  }

  public onClientSelected() {
    this.grid?.refresh();
  }

  public onSearchTextChanged(searchText: string) {
    this.grid.search(searchText);
  }

  protected abstract initializeEditForm(data: any): void;
  protected abstract getEditDialogTitleParams(rowData: any): object;

  // override this method if a more complex method is needed to compare added item with the ones in the rows (added item does not have id set)
  protected addObjectCompare(addedObject: any, rowObject: any): boolean {
    if (this.addIndexFindColumnName) {
      return addedObject[this.addIndexFindColumnName] === rowObject[this.addIndexFindColumnName];
    }
    return false;
  }

  // override this method if different data or data structure has to be provided for add / edit
  protected getDataToSave(data: any): any {
    data = this.gridEditForm.value;
  }

  protected dataSaveCompleteAction(args: any) {
    // data needs to be copied to data source array in case of args.action === edit to avoid issues
    // and then refresh grid
  }

  public actionComplete(args) {
    if (args.requestType === SfRequestTypes.BeginEdit || args.requestType === SfRequestTypes.Add) {
      const dialog = args.dialog;
      dialog.width = 780;
      dialog.header = args.requestType === SfRequestTypes.BeginEdit
        ? this.translateService.instant(this.editDialogTitle, this.getEditDialogTitleParams(args.rowData))
        : this.translateService.instant(this.addDialogTitle);
      if (this.cssClass) {
        dialog.cssClass = (dialog.cssClass?.length > 0 ? dialog.cssClass + " " : "") + this.cssClass;
      }

      const saveBtn = dialog.btnObj[0];
      this.gridEditForm.statusChanges
        .pipe(
          startWith(this.gridEditForm.status),
          takeUntil(this.unsubscribeSubject),
        )
        .subscribe(status => {
          saveBtn.disabled = (status != 'VALID') || (JSON.stringify(this.formInitialValue) === JSON.stringify(this.gridEditForm.value));
        });
    }
    if (args.requestType === SfRequestTypes.Save) {
      this.dataSaveCompleteAction(args);
    }
  }

  public actionBegin(args: SaveEventArgs) {
    if (args.requestType === SfRequestTypes.BeginEdit || args.requestType === SfRequestTypes.Add) {
      this.initializeEditForm(args.rowData);
      this.formInitialValue = this.gridEditForm.value;
    }
    else if (args.requestType === SfRequestTypes.Save) {
      if (this.gridEditForm.valid) {
        args.index = this.data.length;
        this.getDataToSave(args.data);
      } else {
        args.cancel = true;
      }
    } else if (args.requestType === SfRequestTypes.Cancel) {
      this.unsubscribeSubject.next(null);
    }
  }

  public commandClicked(args: any): void {
    // TODO
    /* if (args.commandColumn.buttonOption.iconCss.includes('icon-trash-can')) {
      ModalHelper.confirm(
        this.translateService.instant('i18n.core.modal.delete.title'),
        this.translateService.instant('i18n.core.modal.delete.content'),
        () => this.grid.deleteRecord('sourceName', args.rowData)
      );
      args.cancel = true;
    } */
  }
}
